/**
 * PhaseSwitcher — the Rails-era phase pills restored (estates-enrichment
 * PR-4). Root-first family from the wire's `phases`; the current member is
 * highlighted and not a link. Renders nothing for standalone estates
 * (empty family, collapsed server-side).
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { buildEstateUrl } from '@/shared/lib/estates/url';
import type { EstatePhaseData } from '@/shared/lib/types/estates';
import { projectLocalized } from '@letsgethome/lib/i18n';
import { plaintextString, type Locale } from '@letsgethome/types/branded';

type Props = {
  phases: EstatePhaseData[];
  locale: Locale;
};

export function PhaseSwitcher({ phases, locale }: Props) {
  const t = useTranslations('estates.detail');
  if (phases.length === 0) {
    return null;
  }
  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
      <p className="mb-2 text-sm font-medium text-muted-foreground">{t('phases')}</p>
      <div className="flex flex-wrap gap-2">
        {phases.map((phase) => {
          const name = plaintextString(projectLocalized(phase.name, locale));
          if (phase.isCurrent) {
            return (
              <span
                key={phase.id}
                className="rounded-full bg-success px-4 py-1.5 text-sm font-medium text-success-foreground"
              >
                {name}
              </span>
            );
          }
          return (
            <Link
              key={phase.id}
              href={buildEstateUrl(phase.slug, phase.publicId)}
              prefetch="intent"
              className="rounded-full bg-muted px-4 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/70"
            >
              {name}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
